import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  allowResponseOption,
  attendingResponses,
  getCountLabel,
  getEventResponseCount,
  getResponseIcon,
  getResponseIconWithColor,
  getResponseLabel,
  getResponseLabelPastTense,
  getTotalAttendingEvent,
  getTotalAttendingLabel,
  getTotalNotAttendingEvent,
  notAttendingResponses,
  orderedResponseOptions
} from 'app/events/helpers/event-response-helpers';
import { eventsByDay } from 'app/events/helpers/events-by-day';
import { isPastOrTodayLocal } from 'app/events/helpers/is-past-or-today-local';
import { isSameDayLocal } from 'app/events/helpers/is-same-day-local';
import { CalendarEventResponseSubqueryHelpers } from 'app/query-common/services/CalendarEventResponseSubqueryHelpers';
import { isPast } from 'date-fns';
import {
  CalendarEventDetailsFragment,
  CalendarEventResponseCoreFragment,
  CalendarEventResponseStatusEnum,
  SetCalendarEventResponseGQL
} from 'generated/graphql';
import { QueryStorageService } from 'query-builder/core/query-storage.service';

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class EventListComponent implements OnChanges {
  @Input() events;
  @Input() loading = false;
  @Input() entityId;
  @Input() showRsvps = false;
  @Input() showDateHeader = true;
  @Input() taskButtons = false;
  @Input() showDayCounts = true;
  @Input() expandTagsByDefault = false;
  @Output() eventResponseUpdated: EventEmitter<CalendarEventResponseCoreFragment> = new EventEmitter<CalendarEventResponseCoreFragment>();

  calendarDays = [];
  eventsExpanded = {};
  isSubmitting = false;

  ResponseTypes = CalendarEventResponseStatusEnum;
  getIcon = getResponseIcon;
  getLabel = getResponseLabel;
  getLabelPast = getResponseLabelPastTense;
  getIconWithColor = getResponseIconWithColor;
  getLabelForCount = getCountLabel;
  responseOptions = orderedResponseOptions;
  attendedResponses = attendingResponses;
  notAttendedResponses = notAttendingResponses;
  eventTotalAttending = getTotalAttendingEvent;
  eventTotalNotAttending = getTotalNotAttendingEvent;
  eventResponseCount = getEventResponseCount;
  totalAttendingLabel = getTotalAttendingLabel;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _setCalendarEventResponseGQL: SetCalendarEventResponseGQL,
    private _queryStorageService: QueryStorageService,
  ) {
  }

  get campaignId() {
    return this._route.snapshot.queryParams['campaignId'];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.events) {
      this.calendarDays = eventsByDay(this.events, true);
    }
  }


  dateIsPast(date) {
    return isPast(new Date(date));
  }

  getDayTotal(events) {
    return events.length;
  }

  dayTotalAttending(events) {
    return events.reduce((a, b) => a + getTotalAttendingEvent(b), 0);
  }

  dayTotalNotAttending(events) {
    return events.reduce((a, b) => a + getTotalNotAttendingEvent(b), 0);
  }

  sum(arr, key) {
    return arr.reduce((a, b) => a + getEventResponseCount(b, key), 0);
  }

  isMultipleDays(eventInfo) {
    return eventInfo.endDate &&
      !isSameDayLocal(new Date(eventInfo.startDate), new Date(eventInfo.endDate));
  }

  viewEvent(eventId) {
    this._router.navigate(['/events/view'], {
      queryParams: {
        campaignId: this._route.snapshot.queryParams['campaignId'],
        eventId
      }
    });
  }

  // Update event status on entity profile page and task > other events
  updateEventStatus(eventInfo, response: CalendarEventResponseStatusEnum) {
    this.isSubmitting = true;
    this._setCalendarEventResponseGQL.mutate({
      input: {
        calendarEventId: eventInfo.id,
        entityId: this.entityId,
        status: response,
      },
    }, {
      // Refetch the queries that depend on this response
      refetchQueries: ['GetCalendarEventResponseForEntity']
    }).subscribe(({ data }) => {
      this.eventResponseUpdated.emit(data.setCalendarEventResponse.calendarEventResponse);
      this.isSubmitting = false;
    });
  }

  showAttendanceButtons(eventInfo): boolean {
    const past = isPast(new Date(eventInfo.startDate));
    const today = isSameDayLocal(new Date(eventInfo.startDate), new Date());
    return (past || today) &&
      eventInfo.entityResponse?.status !== CalendarEventResponseStatusEnum.Attended &&
      eventInfo.entityResponse?.status !== CalendarEventResponseStatusEnum.NoShow;
  }

  // Buttons are shown on to-dos unless:
  // - Response updated within the last 24 hours
  // - Response is Attended, No-show, or Declined
  // - Response is Double-confirmed and event has not started yet
  showTaskButtons(eventInfo): boolean {
    // Hide buttons if response is within the last day
    if (eventInfo.entityResponse?.respondedAt) {
      const respondedAt = new Date(eventInfo.entityResponse.respondedAt);
      const now = new Date();
      const diff = now.getTime() - respondedAt.getTime();
      const diffInDays = diff / (1000 * 3600 * 24);
      if (diffInDays < 1) {
        return false;
      }
    }

    switch (eventInfo.entityResponse?.status) {
      case CalendarEventResponseStatusEnum.Attended:
      case CalendarEventResponseStatusEnum.NoShow:
      case CalendarEventResponseStatusEnum.Declined:
        return false;
      case CalendarEventResponseStatusEnum.DoubleConfirmed:
        return this.showAttendanceButtons(eventInfo);
      case CalendarEventResponseStatusEnum.Rsvp:
      case CalendarEventResponseStatusEnum.Tentative:
      case CalendarEventResponseStatusEnum.Confirmed:
      default:
        return true;
    }
  }

  // Main task button icon
  getTaskButtonResponse(eventInfo): CalendarEventResponseStatusEnum {
    if (this.showAttendanceButtons(eventInfo)) {
      return CalendarEventResponseStatusEnum.Attended;
    }
    switch (eventInfo.entityResponse?.status) {
      case CalendarEventResponseStatusEnum.Rsvp:
        return CalendarEventResponseStatusEnum.Confirmed;
      case CalendarEventResponseStatusEnum.Confirmed:
        return CalendarEventResponseStatusEnum.DoubleConfirmed;
      case CalendarEventResponseStatusEnum.DoubleConfirmed:
        return CalendarEventResponseStatusEnum.DoubleConfirmed;
      case CalendarEventResponseStatusEnum.Tentative:
      default:
        return CalendarEventResponseStatusEnum.Rsvp;
    }
  }

  // Perform todo button
  updateEvent(event) {
    const response = this.getTaskButtonResponse(event);
    this.updateEventStatus(event, response);
  }

  // No-show button
  noShowEvent(event) {
    this.updateEventStatus(event, CalendarEventResponseStatusEnum.NoShow);
  }

  showResponseOption(event, option: CalendarEventResponseStatusEnum): boolean {
    return allowResponseOption(event?.entityResponse?.status, option, isPastOrTodayLocal(new Date(event.startDate)));
  }

  runEventResponseQuery(events: CalendarEventDetailsFragment[], responses: CalendarEventResponseStatusEnum[]) {
    const qss = this._queryStorageService;
    const storedQuery = qss.saveQuery(
      this.campaignId,
      CalendarEventResponseSubqueryHelpers.createEventResponseQuery(events.map(e => e.id), responses)
    );

    this._router.navigate(['/entity/list'], {
      queryParams: {
        campaignId: this.campaignId,
        clientQueryId: storedQuery.clientQueryId
      }
    });
  }
}
